import { Button, Icon, IconButton, Stack } from '@mui/material';
import { CreateFacilityItem, Facility, FacilityType } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { categorizeFacilities, getChildrenByParentId } from '@schooly/hooks/use-facilities';
import {
  ArrowAngleIcon,
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  MinusIcon,
  ModalContent,
  ModalHeader,
  ModalMain,
  ModalSmall,
  PlusIcon,
  SimpleButton,
  Spin,
} from '@schooly/style';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { ModalFooter } from '../../../../../components/uikit-components/Modal/Modal.styled';
import { useRouter } from '../../../../../context/router/useRouter';
import { FacilityActionsMenu, FacilityCard, FacilityRow } from './FacilityCard';
import { SchoolGeneralFacilitiesEmptyCard } from './SchoolGeneralFacilitiesEmptyCard';
import {
  createEmptyFacility,
  facilityToFormItem,
  getAllDescendants,
  getDeleteConfirmationTextId,
  useGetFacilityMenuActions,
  validateFacilityName,
} from './utils';

export type FacilityFormItem = CreateFacilityItem & { used_in_timetables: boolean };
export type FacilitiesForm = { facilities: FacilityFormItem[] };
export type FacilityField = FacilityFormItem & { index: number };

type SchoolGeneralFacilitiesModalContentProps = {
  facilities: Facility[];
  onSubmit: (data: FacilitiesForm) => Promise<void>;
  isSaving?: boolean;
};

export const SchoolGeneralFacilitiesModalContent: FC<SchoolGeneralFacilitiesModalContentProps> = ({
  facilities,
  onSubmit,
  isSaving,
}) => {
  const { formatMessage } = useIntl();
  const { goBack } = useRouter();
  const { getConfirmation } = useConfirmationDialog();

  const existingIds = useMemo(() => new Set(facilities.map((f) => f.id)), [facilities]);
  const confirmedNamesRef = useRef(new Map(facilities.map((f) => [f.id, f.name])));

  const form = useForm<FacilitiesForm>({
    defaultValues: { facilities: facilities.map(facilityToFormItem) },
  });
  const hasErrors = !!form.formState.errors.facilities;

  const { append, replace } = useFieldArray({
    control: form.control,
    name: 'facilities',
  });

  const facilitiesForm = useWatch({ control: form.control, name: 'facilities' });
  const { campuses, buildings, standaloneBuildings, standaloneRooms, childrenByParentId } =
    useMemo(() => {
      const facilities = facilitiesForm.map((f, index) => ({ ...f, index }));
      return categorizeFacilities(facilities);
    }, [facilitiesForm]);

  const validateUniqueName = useCallback(
    (index: number) => {
      const result = validateFacilityName(form.getValues('facilities'), index);
      if (typeof result === 'string') {
        return formatMessage({ id: result });
      }
      return true;
    },
    [form, formatMessage],
  );

  const handleAdd = useCallback(
    (type: FacilityType, parentId: string | null = null) => {
      append(createEmptyFacility(type, parentId));
    },
    [append],
  );

  const handleNameBlur = useCallback(
    async (index: number) => {
      const facility = facilitiesForm[index];
      if (!facility) return;
      if (facility.type !== FacilityType.Room || !facility.used_in_timetables) return;

      const confirmedName = confirmedNamesRef.current.get(facility.id);
      if (confirmedName === undefined || facility.name === confirmedName) return;

      const isConfirmed = await getConfirmation({
        textId: 'school-tabs-Facilities-EditConfirmation-room-usedInTimetables',
        textValues: { name: facility.name ? `"${facility.name}"` : '' },
      });

      if (isConfirmed) {
        confirmedNamesRef.current.set(facility.id, facility.name);
      } else {
        form.setValue(`facilities.${index}.name`, confirmedName);
      }
    },
    [facilitiesForm, getConfirmation, form],
  );

  const handleDelete = useCallback(
    async (index: number) => {
      const facility = facilitiesForm[index];
      if (!facility) return;

      const descendants = getAllDescendants(childrenByParentId, facility.id);
      const usedInTimetables =
        facility.type === FacilityType.Room
          ? facility.used_in_timetables
          : descendants.some((d) => d.type === FacilityType.Room && d.used_in_timetables);

      const confirmationTextId = getDeleteConfirmationTextId({
        type: facility.type,
        usedInTimetables,
        isExisting: existingIds.has(facility.id),
        hasChildren: descendants.length > 0,
      });

      if (confirmationTextId) {
        const isConfirmed = await getConfirmation({
          textId: confirmationTextId,
          textValues: { name: facility.name ? `"${facility.name}"` : '' },
        });
        if (!isConfirmed) return;
      }

      const idsToRemove = new Set([facility.id, ...descendants.map((d) => d.id)]);
      replace(facilitiesForm.filter((f) => !idsToRemove.has(f.id)));
      if (hasErrors) form.trigger('facilities');
    },
    [facilitiesForm, childrenByParentId, replace, hasErrors, form, getConfirmation, existingIds],
  );

  const handleChangeParentId = useCallback(
    (index: number, parentId: string | null) => {
      const facilities = form.getValues('facilities');
      const current = facilities[index];
      if (!current) return;

      const updated = [...facilities];
      updated.splice(index, 1);
      updated.push({ ...current, parent_id: parentId });

      replace(updated);
      if (hasErrors) form.trigger('facilities');

      const lastIndex = updated.length - 1;
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLElement>(`[name="facilities.${lastIndex}.name"]`);
        input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    [form, replace, hasErrors],
  );

  const getMenuActions = useGetFacilityMenuActions({
    campuses,
    buildingCount: buildings.length,
    standaloneBuildings,
    childrenByParentId,
    handleAttach: handleChangeParentId,
    handleDetach: (index) => handleChangeParentId(index, null),
  });

  const getRowEndAdornment = (facility: FacilityField) => {
    const actions = getMenuActions(facility);

    return (
      <Stack direction="row" gap={1}>
        {actions.length > 0 && <FacilityActionsMenu actions={actions} />}
        <IconButton inverse onClick={() => handleDelete(facility.index)}>
          {existingIds.has(facility.id) ? <DeleteIcon /> : <MinusIcon />}
        </IconButton>
      </Stack>
    );
  };

  const rowStartAdornment = (
    <Icon sx={{ color: 'common.grey' }}>
      <ArrowAngleIcon />
    </Icon>
  );

  const handleClose = useCallback(async () => {
    if (!form.formState.isDirty) {
      goBack();
      return;
    }
    const isConfirmed = await getConfirmation({
      textId: 'school-edit-CloseUnsavedConfirmation',
    });
    if (isConfirmed) goBack();
  }, [form.formState.isDirty, getConfirmation, goBack]);

  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (!name || !hasErrors) return;

      if (name.split('.')[2] === 'name') {
        form.trigger('facilities');
      }
    });
    return () => subscription.unsubscribe();
  }, [form, hasErrors]);

  return (
    <ModalSmall open onClose={handleClose}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <ModalHeader title={formatMessage({ id: 'school-tabs-Facilities' })} active>
            <IconButton onClick={handleClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>

          <ModalMain>
            <ModalContent active>
              <Stack gap={2}>
                {facilitiesForm.length === 0 ? (
                  <SchoolGeneralFacilitiesEmptyCard handleAddFacility={handleAdd} />
                ) : (
                  <>
                    {campuses.map((campus) => (
                      <FacilityCard
                        key={campus.id}
                        index={campus.index}
                        endAdornment={getRowEndAdornment(campus)}
                        placeholder={formatMessage({
                          id: `school-tabs-Facilities-Campus`,
                        })}
                        validateName={validateUniqueName}
                      >
                        {getChildrenByParentId(childrenByParentId, campus.id).map(
                          (building, idx, arr) => {
                            const isLastBuilding = arr.length - 1 === idx;

                            return (
                              <Stack key={building.id} gap={1}>
                                <FacilityRow
                                  index={building.index}
                                  startAdornment={rowStartAdornment}
                                  endAdornment={getRowEndAdornment(building)}
                                  placeholder={formatMessage({
                                    id: `school-tabs-Facilities-Building`,
                                  })}
                                  validateName={validateUniqueName}
                                />
                                <Stack gap={1} pl={3.5}>
                                  {getChildrenByParentId(childrenByParentId, building.id).map(
                                    (room) => (
                                      <FacilityRow
                                        key={room.id}
                                        index={room.index}
                                        startAdornment={rowStartAdornment}
                                        endAdornment={getRowEndAdornment(room)}
                                        placeholder={formatMessage({
                                          id: `school-tabs-Facilities-Room`,
                                        })}
                                        validateName={validateUniqueName}
                                        onBlur={handleNameBlur}
                                      />
                                    ),
                                  )}
                                  <SimpleButton
                                    onClick={() => handleAdd(FacilityType.Room, building.id)}
                                    startIcon={<PlusIcon />}
                                    sx={{
                                      alignSelf: 'flex-start',
                                      mt: 1,
                                      mb: isLastBuilding ? 0 : 1,
                                    }}
                                  >
                                    {formatMessage({ id: 'school-tabs-Facilities-AddRoom' })}
                                  </SimpleButton>
                                </Stack>
                              </Stack>
                            );
                          },
                        )}
                        <SimpleButton
                          onClick={() => handleAdd(FacilityType.Building, campus.id)}
                          startIcon={<PlusIcon />}
                          sx={{ alignSelf: 'flex-start', mt: 1 }}
                        >
                          {formatMessage({ id: 'school-tabs-Facilities-AddBuilding' })}
                        </SimpleButton>
                      </FacilityCard>
                    ))}

                    {standaloneBuildings.map((building) => (
                      <FacilityCard
                        key={building.id}
                        index={building.index}
                        endAdornment={getRowEndAdornment(building)}
                        placeholder={formatMessage({ id: `school-tabs-Facilities-Building` })}
                        validateName={validateUniqueName}
                      >
                        <Stack gap={1}>
                          {getChildrenByParentId(childrenByParentId, building.id).map((room) => (
                            <FacilityRow
                              key={room.id}
                              index={room.index}
                              startAdornment={rowStartAdornment}
                              endAdornment={getRowEndAdornment(room)}
                              placeholder={formatMessage({ id: 'school-tabs-Facilities-Room' })}
                              validateName={validateUniqueName}
                              onBlur={handleNameBlur}
                            />
                          ))}
                          <SimpleButton
                            onClick={() => handleAdd(FacilityType.Room, building.id)}
                            startIcon={<PlusIcon />}
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                          >
                            {formatMessage({ id: 'school-tabs-Facilities-AddRoom' })}
                          </SimpleButton>
                        </Stack>
                      </FacilityCard>
                    ))}

                    {standaloneRooms.length > 0 && (
                      <Stack gap={1}>
                        {standaloneRooms.map((room) => (
                          <FacilityRow
                            key={room.id}
                            index={room.index}
                            endAdornment={getRowEndAdornment(room)}
                            placeholder={formatMessage({ id: 'school-tabs-Facilities-Room' })}
                            validateName={validateUniqueName}
                            onBlur={handleNameBlur}
                          />
                        ))}
                      </Stack>
                    )}

                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Button
                        variant="outlined"
                        startIcon={<PlusIcon />}
                        onClick={() => handleAdd(FacilityType.Campus)}
                      >
                        {formatMessage({ id: 'school-tabs-Facilities-AddCampus' })}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PlusIcon />}
                        onClick={() => handleAdd(FacilityType.Building)}
                      >
                        {formatMessage({ id: 'school-tabs-Facilities-AddBuilding' })}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PlusIcon />}
                        onClick={() => handleAdd(FacilityType.Room)}
                      >
                        {formatMessage({ id: 'school-tabs-Facilities-AddRoom' })}
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </ModalContent>
          </ModalMain>

          <ModalFooter active>
            <Button type="submit" endIcon={isSaving ? <Spin /> : <CheckIcon />} disabled={isSaving}>
              {formatMessage({ id: 'action-Save' })}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
